import { isAxiosError } from 'axios';
import { formErrorsSchema } from '@/common/api/schemas.ts';
import { t } from '@lingui/core/macro';
import { z, ZodObject } from 'zod';
import { formOptions } from '@tanstack/react-form';

export interface AppFormOptions<TSchema extends ZodObject> {
  schema: TSchema;
  defaultValues: z.input<TSchema>;
  onSubmit: (options: { value: z.output<TSchema> }) => unknown | Promise<unknown>;
}

export function appFormOptions<TSchema extends ZodObject>({
  schema,
  defaultValues,
  onSubmit,
}: AppFormOptions<TSchema>) {
  return formOptions({
    defaultValues: defaultValues,
    canSubmitWhenInvalid: true,
    validators: {
      onChange: schema,
      onSubmitAsync: async (props) => {
        try {
          const parsedValues = schema.parse(props.value);
          await onSubmit({ value: parsedValues });
        } catch (e: unknown) {
          if (isAxiosError(e)) {
            if (e.status === 400) {
              const formErrors = formErrorsSchema.parse(e.response?.data);

              if (Object.keys(formErrors.errors).some((key) => key in defaultValues))
                return {
                  fields: formErrors.errors,
                };
            }
          }

          return { form: t`Error occurred during form submission. Please try again later.` };
        }

        return undefined;
      },
    },
    onSubmitInvalid: () => {
      const firstAlert = document.querySelector('form *[role="alert"]');
      if (firstAlert) {
        firstAlert.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      const firstInvalidInput = document.querySelector(
        'input[aria-invalid="true"]'
      ) as HTMLInputElement;
      if (firstInvalidInput) {
        firstInvalidInput.focus();

        if (!firstAlert) {
          firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    },
  });
}
